import { createContext, useCallback, useContext, useEffect, useState } from "react";
import {
  authGoogleSession,
  authLogin,
  authLogout,
  authMe,
  authRegister,
  getToken,
  setToken,
  setUnauthorizedHandler,
} from "@/lib/api";
import { closeAuthBrowser, getRedirectBase, isNativePlatform, onDeepLink, openAuthUrl, parseSessionId } from "@/lib/native";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// URL del login gestito (Google) di Emergent. Ritorna a <redirect>#session_id=...
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com/";

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const clearSession = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => clearSession());
  }, [clearSession]);

  // Completa la sessione Google a partire da un session_id (web hash o deep link nativo).
  const completeGoogleSession = useCallback(async (sessionId) => {
    try {
      const res = await authGoogleSession(sessionId);
      setToken(res.token);
      setUser(res.user);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    (async () => {
      // 1) Callback Google (Emergent) sul WEB: #session_id=...
      const sessionId = parseSessionId(window.location.hash || "");
      if (sessionId) {
        await completeGoogleSession(sessionId);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
        setLoading(false);
        return;
      }
      // 2) Token salvato -> valida con /auth/me
      if (getToken()) {
        try {
          const me = await authMe();
          setUser(me.user);
        } catch {
          clearSession();
        }
      }
      setLoading(false);
    })();
  }, [clearSession, completeGoogleSession]);

  // Deep link nativo (Universal/App Link): <base>/#session_id=... dopo il login Google.
  useEffect(() => {
    const off = onDeepLink(async (url) => {
      const sessionId = parseSessionId(url);
      if (sessionId) {
        await completeGoogleSession(sessionId);
        await closeAuthBrowser();
      }
    });
    return off;
  }, [completeGoogleSession]);

  const login = async (email, password) => {
    const res = await authLogin(email, password);
    setToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const register = async (email, password, name) => {
    const res = await authRegister(email, password, name);
    setToken(res.token);
    setUser(res.user);
    return res.user;
  };

  const loginWithGoogle = async () => {
    const redirect = getRedirectBase() + "/";
    await openAuthUrl(`${EMERGENT_AUTH_URL}?redirect=${encodeURIComponent(redirect)}`);
  };

  const logout = async () => {
    try {
      await authLogout();
    } catch {
      /* ignora */
    }
    clearSession();
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, isAuthenticated: !!user, login, register, loginWithGoogle, logout, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};
